import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { ProductCard } from '../components/ProductCard';
import { products } from '../utils/api';
import { SEO } from '../components/SEO';
import { RecentlyViewed } from '../components/RecentlyViewed';
import { ArrowRight, Star, Shield, Truck, Clock, RefreshCw } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: 'easeOut' },
  }),
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

const TRUST_ITEMS = [
  { icon: Shield, text: '100% Authentic Vintage' },
  { icon: Clock, text: 'Dispatched in 48 Hours' },
  { icon: RefreshCw, text: 'Free 14-Day Returns' },
  { icon: Star, text: 'One-of-a-Kind Only' },
];

const WORN_LOVED = [
  { img: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=500&h=500&fit=crop', quote: '"Feels like it was made for me."', name: 'Emma, London' },
  { img: 'https://images.unsplash.com/photo-1529139574466-a303027c1d8b?w=500&h=500&fit=crop', quote: '"Best vintage find I\'ve ever had."', name: 'Luca, Milan' },
  { img: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=500&h=500&fit=crop', quote: '"Wore it to a shoot. Everyone asked."', name: 'Sophie, Paris' },
  { img: 'https://images.unsplash.com/photo-1552374196-1ab2a1c593e8?w=500&h=500&fit=crop', quote: '"One-off pieces like nowhere else."', name: 'Daniel, Berlin' },
  { img: 'https://images.unsplash.com/photo-1534030347209-467a5b0ad3e6?w=500&h=500&fit=crop', quote: '"Quality you can feel instantly."', name: 'Maya, Barcelona' },
  { img: 'https://images.unsplash.com/photo-1488161628813-04466f872be2?w=500&h=500&fit=crop', quote: '"My go-to for authentic vintage."', name: 'James, Manchester' },
];

export const Home = () => {
  const [newDrops, setNewDrops] = useState([]);
  const [rareFinds, setRareFinds] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const data = await products.getAll();
        setNewDrops(data.slice(0, 6));
        const lowStock = data.filter(p => p.stock <= 2).slice(0, 6);
        setRareFinds(lowStock.length >= 3 ? lowStock : data.slice(2, 8));
        setBestSellers(data.slice(4, 10).length >= 4 ? data.slice(4, 10) : data.slice(0, 6));
      } catch (error) {
        console.error('Failed to load products', error);
      }
    };
    loadProducts();
  }, []);

  return (
    <div className="w-full" data-testid="home-page">
      <SEO path="/" />

      {/* ─── HERO ─── */}
      <section className="relative min-h-[90vh] flex items-end overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_6b1653b0-543f-41b7-adb3-d521cbe0d309/artifacts/wkx9auri_ChatGPT%20Image%20Mar%2021%2C%202026%2C%2006_19_04%20PM.png"
            alt="Vintage Fashion Collection"
            className="w-full h-full object-cover"
            loading="eager"
            width={1440}
            height={900}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
        </div>
        <div className="relative z-10 w-full pb-12 md:pb-20 px-6 md:px-12 max-w-[1400px] mx-auto">
          <motion.div className="max-w-2xl" initial="hidden" animate="visible" variants={stagger}>
            <motion.h1
              variants={fadeUp} custom={0}
              className="text-[32px] sm:text-5xl md:text-6xl font-heading font-bold text-white leading-[1.05] tracking-tight mb-3"
              data-testid="hero-title"
            >
              One-of-a-kind vintage.<br />
              <span className="text-white/80">Never restocked.</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={1} className="text-white/60 text-sm md:text-base max-w-md mb-8 leading-relaxed" data-testid="hero-subtext">
              Every piece is sourced, graded, and gone forever. Shop before someone else does.
            </motion.p>
            <motion.div variants={fadeUp} custom={2} className="flex flex-col sm:flex-row gap-3">
              <Link to="/shop">
                <button className="w-full sm:w-auto bg-white text-[#0A0A0A] px-10 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#C9A84C] hover:text-white transition-all duration-300" data-testid="hero-cta-shop">
                  Shop Now
                </button>
              </Link>
              <Link to="/about">
                <button className="w-full sm:w-auto bg-transparent border border-white/60 text-white px-10 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-white hover:text-[#0A0A0A] transition-all duration-300" data-testid="hero-cta-about">
                  Our Story
                </button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─── TRUST STRIP — clean, no background ─── */}
      <div className="py-5 border-b border-[#E2E2E2]" data-testid="trust-strip">
        <div className="container-custom">
          <div className="flex md:grid md:grid-cols-4 gap-6 md:gap-4 overflow-x-auto scrollbar-hide md:overflow-visible items-center justify-center md:justify-between">
            {TRUST_ITEMS.map(({ icon: Icon, text }, i) => (
              <div key={i} className="flex items-center gap-2.5 flex-shrink-0" data-testid={`trust-item-${i}`}>
                <Icon className="w-4 h-4 text-[#C9A84C] flex-shrink-0" strokeWidth={1.5} />
                <span className="text-[11px] text-[#0A0A0A] tracking-[0.05em] whitespace-nowrap font-semibold">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── JUST DROPPED ─── */}
      <section className="py-12 md:py-16 bg-[#F9F8F6]" data-testid="new-drops-section">
        <div className="container-custom">
          <SectionHeader tag="Just Dropped" title="New Arrivals" link="/shop?sort=newest" testId="new-drops" />
          <ProductGrid products={newDrops} cols="grid-cols-2 md:grid-cols-3" testId="new-drops-grid" />
        </div>
      </section>

      {/* ─── WORN & LOVED (Social Proof) ─── */}
      <section className="py-12 md:py-16 bg-[#F0EEEB]" data-testid="worn-loved-section">
        <div className="container-custom">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="text-[9px] uppercase tracking-[0.35em] text-[#C9A84C] font-semibold block mb-1">Social Proof</span>
              <h2 className="text-2xl md:text-4xl font-heading font-bold tracking-tight leading-none" data-testid="worn-loved-title">Worn & Loved</h2>
            </div>
            <div className="flex items-center gap-1 bg-white px-3 py-1.5 rounded-full border border-[#E2E2E2]">
              <Star className="w-4 h-4 fill-[#C9A84C] text-[#C9A84C]" />
              <span className="text-sm font-bold">4.9</span>
              <span className="text-xs text-[#666] hidden sm:inline">(200+)</span>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6" data-testid="worn-loved-grid">
            {WORN_LOVED.map((item, i) => (
              <div key={i} className="group" data-testid={`worn-loved-${i}`}>
                <div className="aspect-square overflow-hidden mb-3 bg-[#E2E2E2]">
                  <img
                    src={item.img}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    width={500}
                    height={500}
                  />
                </div>
                <p className="text-sm italic text-[#0A0A0A] leading-snug mb-1">{item.quote}</p>
                <p className="text-[10px] uppercase tracking-[0.12em] font-bold text-[#666]">— {item.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── RARE FINDS ─── */}
      <section className="py-12 md:py-16 bg-[#F9F8F6]" data-testid="rare-finds-section">
        <div className="container-custom">
          <SectionHeader tag="Limited Stock" title="Rare Finds" link="/shop" tagColor="text-red-600" testId="rare-finds" />
          <ProductGrid products={rareFinds} cols="grid-cols-2 md:grid-cols-3" testId="rare-finds-grid" />
        </div>
      </section>

      {/* ─── BRAND TEASER ─── */}
      <section className="py-12 md:py-16 bg-[#F0EEEB]" data-testid="brand-teaser-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
            <motion.div
              className="aspect-square overflow-hidden"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_6b1653b0-543f-41b7-adb3-d521cbe0d309/artifacts/bupqsq2p_ChatGPT%20Image%20Mar%2021%2C%202026%2C%2005_25_50%20PM.png"
                alt="Vintage curation"
                className="w-full h-full object-cover"
                loading="lazy"
                width={700}
                height={700}
              />
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.span variants={fadeUp} className="text-[9px] uppercase tracking-[0.35em] text-[#C9A84C] font-semibold block mb-2">Our Story</motion.span>
              <motion.h2 variants={fadeUp} custom={1} className="text-2xl md:text-4xl font-heading font-bold tracking-tight mb-4 leading-tight" data-testid="brand-teaser-title">
                We source the pieces<br />others can't find.
              </motion.h2>
              <motion.p variants={fadeUp} custom={2} className="text-[#666] text-base leading-relaxed mb-6 max-w-md">
                12 years in flea markets, warehouses, and private collections across Europe. Every item is handpicked, graded honestly, and chosen because it deserves to be worn again. Not resold. Worn.
              </motion.p>
              <motion.div variants={fadeUp} custom={3}>
                <Link to="/about" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-bold text-[#0A0A0A] hover:text-[#C9A84C] transition-colors border-b border-[#0A0A0A] hover:border-[#C9A84C] pb-0.5" data-testid="brand-teaser-cta">
                  Read Our Story <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─── BRANDS MARQUEE ─── */}
      <div className="bg-[#0A0A0A] py-6 overflow-hidden" data-testid="brands-marquee-section">
        <div className="text-center mb-3">
          <span className="text-[8px] uppercase tracking-[0.4em] text-[#C9A84C]/50">Brands We Stock</span>
        </div>
        <Marquee speed={30} gradient={false} direction="right">
          {[
            "LEVI'S", 'NIKE', 'ADIDAS', 'TOMMY HILFIGER', 'RALPH LAUREN', 'CARHARTT',
            'CHAMPION', 'THE NORTH FACE', 'PATAGONIA', 'CONVERSE', 'VANS', 'PUMA',
            'FILA', 'LACOSTE', 'CALVIN KLEIN', 'TIMBERLAND', 'BURBERRY', 'DICKIES',
            'STUSSY', 'SUPREME', 'G-STAR RAW', 'FRED PERRY',
          ].map((name, i) => (
            <div key={i} className="mx-8 flex items-center select-none">
              <span className="text-base md:text-lg tracking-[0.1em] font-heading font-bold text-white/25 hover:text-[#C9A84C] transition-colors duration-400 whitespace-nowrap">
                {name}
              </span>
              <span className="ml-8 text-[#C9A84C]/20 text-[8px]">&#9830;</span>
            </div>
          ))}
        </Marquee>
      </div>

      {/* ─── BEST SELLERS ─── */}
      <section className="py-12 md:py-16 bg-[#F9F8F6]" data-testid="best-sellers-section">
        <div className="container-custom">
          <SectionHeader tag="Popular This Week" title="Best Sellers" link="/shop" testId="best-sellers" />
          <ProductGrid products={bestSellers} cols="grid-cols-2 md:grid-cols-3" testId="best-sellers-grid" />
        </div>
      </section>

      {/* ─── RECENTLY VIEWED ─── */}
      <section className="bg-[#F9F8F6]">
        <div className="container-custom">
          <RecentlyViewed />
        </div>
      </section>

      {/* ─── FINAL TRUST ─── */}
      <section className="py-8 bg-[#F0EEEB] border-y border-[#E2E2E2]" data-testid="values-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Shield, label: 'Authentic Vintage', desc: 'Genuine & verified pieces' },
              { icon: Star, label: 'Quality Checked', desc: 'Inspected before listing' },
              { icon: Clock, label: '12+ Years', desc: 'Curating since 2012' },
              { icon: Truck, label: 'Fast Shipping', desc: 'Tracked delivery 3-5 days' },
            ].map(({ icon: Icon, label, desc }, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`value-${i}`}>
                <Icon className="h-5 w-5 text-[#C9A84C] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wide">{label}</h3>
                  <p className="text-[11px] text-[#666] mt-0.5">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── BRAND STORY CTA ─── */}
      <section className="py-14 md:py-20 bg-[#0A0A0A]" data-testid="brand-story-section">
        <div className="container-custom max-w-3xl text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} custom={1} className="text-2xl md:text-4xl font-heading font-bold text-white tracking-tight mb-4">
              Every piece tells a story.<br />Now it's yours.
            </motion.h2>
            <motion.div variants={fadeUp} custom={2}>
              <Link to="/shop">
                <button className="bg-white text-[#0A0A0A] px-10 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#C9A84C] hover:text-white transition-all duration-300" data-testid="brand-story-cta">
                  Shop Now
                </button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

/* ─── REUSABLE COMPONENTS ─── */

const SectionHeader = ({ tag, title, link, tagColor = 'text-[#C9A84C]', testId }) => (
  <div className="flex items-end justify-between mb-6 gap-3">
    <div>
      <span className={`text-[9px] uppercase tracking-[0.3em] ${tagColor} font-semibold block`} data-testid={`${testId}-label`}>{tag}</span>
      <h2 className="text-2xl md:text-4xl font-heading font-bold tracking-tight mt-1 leading-none" data-testid={`${testId}-title`}>{title}</h2>
    </div>
    <Link to={link} className="group flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-[#666] hover:text-[#C9A84C] transition-colors flex-shrink-0" data-testid={`${testId}-view-all`}>
      View All <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
    </Link>
  </div>
);

const ProductGrid = ({ products: prods, cols = 'grid-cols-2 md:grid-cols-3', testId }) => (
  <motion.div
    className={`grid ${cols} gap-x-4 md:gap-x-6 gap-y-6 md:gap-y-8`}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-80px" }}
    variants={stagger}
    data-testid={testId}
  >
    {prods.map((product) => (
      <motion.div key={product.id} variants={fadeUp}>
        <ProductCard product={product} />
      </motion.div>
    ))}
  </motion.div>
);
